<template>
  <div class="wrapper text-sm">
    <div>
      <div class="flex items-center justify-between mb-10">
        <h1 class="font-bold capitalize">{{ $t('ui.entities.locationSelection.label') }}</h1>
        <Icon
          v-if="accessSelected.dealerships.length > 0"
          type="times"
          class="text-3xl cursor-pointer"
          @click="closeView"
        ></Icon>
      </div>

      <Accordion>
        <AccordionItem
          v-model:is-open="accordionState.regions"
          :title="$t('ui.entities.locationSelection.region', 2)"
        >
          <div
            v-if="accessSelector.regions.length > 1"
            class="mb-2 text-secondary"
          >
            <Checkbox
              :is-checked="areAllSelected('regions')"
              :label="areAllSelected('regions') ? $t('ui.actions.deselectAll') : $t('ui.actions.selectAll')"
              checkbox-color="utility-gray-500"
              @change="selectAll('regions')"
            />
          </div>
          <div
            v-for="item in accessSelector.regions"
            :key="item.id"
            class="mb-1"
          >
            <Checkbox
              :is-checked="accessSelected.regions.includes(item.id)"
              :label="item.name"
              @change="toggleItem('regions', item.id)"
            />
          </div>
          <div v-if="accessSelector.regions.length === 0">{{ $t('ui.support.assistance') }}</div>
        </AccordionItem>

        <AccordionItem
          v-model:is-open="accordionState.manufacturers"
          :title="$t('ui.entities.manufacturer', 2)"
        >
          <div
            v-if="accessSelector.manufacturers.length > 1"
            class="mb-2 text-secondary"
          >
            <Checkbox
              :is-checked="areAllSelected('manufacturers')"
              :label="areAllSelected('manufacturers') ? $t('ui.actions.deselectAll') : $t('ui.actions.selectAll')"
              checkbox-color="utility-gray-500"
              @change="selectAll('manufacturers')"
            />
          </div>
          <div
            v-for="item in accessSelector.manufacturers"
            :key="'manufacturer-' + item.id"
            class="mb-1"
          >
            <Checkbox
              :is-checked="accessSelected.manufacturers.includes(item.id)"
              :label="item.name"
              @change="toggleItem('manufacturers', item.id)"
            />
          </div>
          <div
            v-if="accessSelector.manufacturers.length === 0"
            class="text-secondary"
          >
            {{
              $t(
                'ui.entities.locationSelection.sentences.selectRegions',
                {
                  item: $t('ui.entities.locationSelection.region', 2)
                },
                2
              )
            }}
          </div>
        </AccordionItem>

        <AccordionItem
          v-model:is-open="accordionState.groups"
          :title="$t('ui.entities.group', 2)"
        >
          <div
            v-if="accessSelector.groups.length > 1"
            class="mb-2 text-secondary"
          >
            <Checkbox
              :is-checked="areAllSelected('groups')"
              :label="areAllSelected('groups') ? $t('ui.actions.deselectAll') : $t('ui.actions.selectAll')"
              checkbox-color="utility-gray-500"
              @change="selectAll('groups')"
            />
          </div>
          <div
            v-for="item in accessSelector.groups"
            :key="'group-' + item.id"
            class="mb-1"
          >
            <Checkbox
              :is-checked="accessSelected.groups.includes(item.id)"
              :label="item.name"
              @change="toggleItem('groups', item.id)"
            />
          </div>
          <div
            v-if="accessSelector.groups.length === 0"
            class="text-secondary"
          >
            {{
              $t(
                'ui.entities.locationSelection.sentences.selectManufacturers',
                {
                  item: $t('ui.entities.manufacturer', 2)
                },
                2
              )
            }}
          </div>
        </AccordionItem>

        <AccordionItem
          v-model:is-open="accordionState.dealerships"
          :title="$t('ui.entities.dealership', 2)"
        >
          <div
            v-if="accessSelector.dealerships.length > 1"
            class="mb-2 text-secondary"
          >
            <Checkbox
              :is-checked="areAllSelected('dealerships')"
              :label="areAllSelected('dealerships') ? $t('ui.actions.deselectAll') : $t('ui.actions.selectAll')"
              checkbox-color="utility-gray-500"
              @change="selectAll('dealerships')"
            />
          </div>
          <div
            v-for="item in accessSelector.dealerships"
            :key="'dealership-' + item.id"
            class="mb-1"
          >
            <Checkbox
              :is-checked="accessSelected.dealerships.includes(item.id)"
              :label="item.name"
              @change="toggleItem('dealerships', item.id)"
            />
          </div>
          <div
            v-if="accessSelector.dealerships.length === 0"
            class="text-secondary"
          >
            {{ $t('ui.entities.locationSelection.sentences.selectGroups', { item: $t('ui.entities.group', 2) }, 2) }}
          </div>
        </AccordionItem>
      </Accordion>
      <Button
        :label="$t('ui.actions.save')"
        :disabled="accessSelected.dealerships.length > 0 ? false : true"
        class="mt-10"
        @click="saveChanges"
      />
    </div>
  </div>
</template>

<script setup>
import { useRouter } from 'vue-router'
import { ref, computed, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'

// stores
import { useAuthStore } from '@/stores/AuthStore.ts'
import { useAccessStore } from '@/stores/AccessStore.ts'
import updateAbility from '@/config/update-ability.js'

import Icon from '@/components/icon/Icon.vue'
import Accordion from '@/components/accordion/Accordion.vue'
import AccordionItem from '@/components/accordion/AccordionItem.vue'
import Checkbox from '@/components/forms/Checkbox.vue'
import Button from '@/components/button/Button.vue'

const router = useRouter()
const authStore = useAuthStore()
const accessStore = useAccessStore()

onMounted(async () => {
  const response = await authStore.verify()
  if (response.success) {
    updateAbility(router)
  } else {
    authStore.signOut()
  }
})

const accordionState = ref({
  regions: true,
  manufacturers: true,
  groups: true,
  dealerships: true
})

// Use storeToRefs to make store state reactive
const { accessAllowed, accessSelected } = storeToRefs(accessStore)

// Computed should check if the value is in accessSelected
const accessSelector = computed(() => {
  // Loop through all items in accessAllowed, and check if the value is present in accessSelected.
  const newArray = {}
  newArray.regions = accessAllowed.value.regions.map(obj => {
    return {
      ...obj,
      isSelected: accessSelected.value.regions.includes(obj.id)
    }
  })
  newArray.manufacturers = accessAllowed.value.manufacturers.map(obj => {
    return {
      ...obj,
      isSelected: accessSelected.value.manufacturers.includes(obj.id)
    }
  })
  newArray.groups = accessAllowed.value.groups.map(obj => {
    return {
      ...obj,
      isSelected: accessSelected.value.groups.includes(obj.id)
    }
  })
  newArray.dealerships = accessAllowed.value.dealerships.map(obj => {
    return {
      ...obj,
      isSelected: accessSelected.value.dealerships.includes(obj.id)
    }
  })

  accessAllowed.value.manufacturers.forEach(man => {
    // Check if the manufacturer should be visible in the DOM or not
    man.isVisible = man.regions.some(manRegion => accessSelected.value.regions.includes(manRegion))
    const isSelected = accessSelected.value.manufacturers.includes(man.id)

    // If is Selected, and should not be visible, remove is selected
    if (isSelected && !man.isVisible) {
      // Remove from accessSelected
      toggleItem('manufacturers', man.id, false)
    }
  })
  // Filter out any that isVisible=false
  newArray.manufacturers = accessAllowed.value.manufacturers.filter(man => man.isVisible)

  newArray.groups = accessAllowed.value.groups.map(obj => {
    return {
      ...obj,
      isSelected: accessSelected.value.groups.includes(obj.id)
    }
  })

  accessAllowed.value.groups.forEach(group => {
    // Determine if region match
    const regionMatch = group.regions.some(region => accessSelected.value.regions.includes(region))

    // Determine if manufacturer match
    const manufacturerMatch = group.manufacturers.some(man => accessSelected.value.manufacturers.includes(man))

    // Check both matches
    if (regionMatch && manufacturerMatch) {
      group.isVisible = true
    } else {
      group.isVisible = false
    }

    const isSelected = accessSelected.value.groups.includes(group.id)

    // If is Selected, and should not be visible, remove is selected
    if (isSelected && !group.isVisible) {
      toggleItem('groups', group.id, false)
    }
  })

  // Filter out any that isVisible=false
  newArray.groups = accessAllowed.value.groups.filter(group => group.isVisible)

  newArray.dealerships = accessAllowed.value.dealerships.map(obj => {
    return {
      ...obj,
      isSelected: accessSelected.value.dealerships.includes(obj.id)
    }
  })

  accessAllowed.value.dealerships.forEach(dealership => {
    const dealershipGroup = dealership.groupId

    // Determine if manufacturer match
    const manufacturerMatch = dealership.manufacturers.some(man => accessSelected.value.manufacturers.includes(man))

    // Determine if group match
    const groupMatch = accessSelected.value.groups.includes(dealershipGroup)
    // console.log(dealership.name, manufacturerMatch, groupMatch);

    // Check both matches
    if (groupMatch && manufacturerMatch) {
      dealership.isVisible = true
    } else {
      dealership.isVisible = false
    }
    const isSelected = accessSelected.value.dealerships.includes(dealership.id)

    // If is Selected, and should not be visible, remove is selected
    if (isSelected && !dealership.isVisible) {
      toggleItem('dealerships', dealership.id, false)
      dealership.isSelected = false
    }
  })

  // Filter out any that isVisible=false
  newArray.dealerships = accessAllowed.value.dealerships.filter(dealership => dealership.isVisible)

  return newArray
})

// Keep accordion open state in sync with available items
watch(
  accessSelector,
  newArray => {
    try {
      accordionState.value.regions = newArray.regions.length > 1
      accordionState.value.manufacturers = newArray.manufacturers.length > 1
      accordionState.value.groups = newArray.groups.length > 1
      accordionState.value.dealerships = newArray.dealerships.length > 1
    } catch (error) {
      console.error('Failed to update accordion open state', error)
    }
  },
  { immediate: true, deep: true }
)

function toggleItem(category, itemId, value) {
  let newValue
  if (value != null) {
    newValue = value
  } else {
    newValue = !accessSelected.value[category].includes(itemId)
  }

  // console.log(newValue)

  if (newValue) {
    // Add item to array
    accessSelected.value[category].push(itemId)
  } else {
    // Remove item from array
    const index = accessSelected.value[category].indexOf(itemId)
    if (index > -1) {
      accessSelected.value[category].splice(index, 1)
    }
  }
}

function closeView() {
  if (authStore.details.userType !== 0) {
    router.push('/')
  } else {
    router.back()
  }
}

function saveChanges() {
  // Store automatically persists on changes, but ensure it's saved
  accessStore.persistToLocalStorage()
  closeView()
}

function areAllSelected(category) {
  if (accessSelected.value[category].length > 0) {
    return accessSelector.value[category].every(item => accessSelected.value[category].includes(item.id))
  } else {
    return false
  }
}

function selectAll(category) {
  // console.log('Selecting All ' + category)

  // If all items are already selected, unselect them
  if (areAllSelected(category)) {
    accessSelector.value[category].forEach(item => {
      toggleItem(category, item.id, false)
    })
  } else {
    // Otherwise, select all items
    accessSelector.value[category].forEach(item => {
      if (!accessSelected.value[category].includes(item.id)) {
        // accessSelected.value[category].push(item.id)
        toggleItem(category, item.id, true)
      }
    })
  }
}
</script>

<style scoped>
body {
  min-height: 100%;
}

.wrapper {
  padding: 3rem;

  inline-size: 100%;
  margin-inline: auto;
  max-inline-size: 600px;
}
</style>
